import React from 'react';
import { Skeleton, Stack, Paper, useTheme } from '@mui/material';
import Panel from '../components/common/Panel';
import { SP } from '../theme/tokens';

/**
 * KpiCardsSkeleton — Ladezustand einer KENNZAHL-KACHELREIHE.
 *
 * Der Ersatz fuer den zentrierten Kreisel an Stellen, an denen die ganze
 * Flaeche wartet und geladen eine Reihe gleich breiter Kennzahl-Kacheln steht
 * (`rma/ProductInquiryAnalytics.jsx`, `rma/RMASlaOverview.jsx`,
 * `rma/RMAReports.jsx`, `pages/taxonomy/{Relations,Collections}ManagerTab.jsx`
 * — `Panel`-Kacheln).
 *
 * 🔴 **Der `paper`-Zweig hat seit dem 2026-10-24 keinen Produktionsaufrufer
 * mehr.** Sein letzter war `ops/LsmWatcher.jsx`: dessen geladene Flaeche ist
 * mit G13a-2 auf `KpiRow`/`StatTile` gezogen (randlose Zahlenreihe), und das
 * Geruest zeichnet seit der Nachbesserung 3 dieselbe Form mit vier
 * `StatTile … loading` — eine `Paper`-Kachelreihe gibt es dort nicht mehr.
 * Der Zweig bleibt trotzdem stehen: er ist ein geteilter Baustein, `L16` prueft
 * ihn, und ein Rueckbau gehoert in eine eigene Story, nicht in eine
 * Nachbesserung (Regel 15 §Knowledge Capture Gap). Der Rueckbau ist als Karte
 * abgelegt und wird von dort verfolgt:
 * `docs/backlog-auto-repair/2026-10-24-kpicardsskeleton-paper-ohne-aufrufer.md`.
 *
 * 🔴 Die Kachelform ist die des Aufrufers, nicht eine zweite: `paper` schaltet
 * auf `Paper variant="outlined"` um, sonst steht die Hausflaeche `Panel`. Eine
 * nachgebaute Reihe wiche beim Umbruch von ihrer geladenen Flaeche ab — genau
 * der Sprung, den das Geruest verhindern soll (dieselbe Lehre wie `KpiRow`,
 * `component-registry.md` §KpiRow).
 *
 * 🔴 Der `paper`-Zweig traegt den RADIUS der geladenen Kachel, nicht den
 * MUI-Vorgabewert. Sein Vorbild war die `StatCard` in `ops/LsmWatcher.jsx`
 * (`borderRadius: 2` — in `sx` ein VIELFACHES von `theme.shape.borderRadius`,
 * 2 × 6 = 12 px, `theme.js:323`); die Form ist mit G13a-2 geloescht, der Zweig
 * traegt ihren Radius als das, was er abbildet. Ohne die Zeile bekaeme die
 * Kachel den Vorgabewert (≤ 6 px). Geschwisterfall: `TableSkeleton`s
 * `paper`-Zweig (`ccff266f64`), samt derselben Falle — eine ZAHL in
 * `sx.borderRadius` multipliziert MUI selbst noch einmal mit
 * `shape.borderRadius` (`@mui/system` `createUnaryUnit`); deshalb steht der
 * Radius als PIXEL-Zeichenkette. Probe: `ladezustaende.test.jsx` L16 am
 * berechneten Stil.
 *
 * 🔴 KEIN erfundener Text — nur Balken; die Beschriftung ist ein Prop der
 * geladenen Kachel und steht im Ladezustand nicht fest.
 *
 * 🔴 Jede Kachel traegt `data-skelett-kachel`. Das ist der stabile Zaehlhaken
 * der Form-Probe (`tests/design/skelett-anatomie.spec.ts`, Faelle `S.1`): sie
 * liest die Kachelzahl des Geruests und vergleicht sie mit der geladenen Reihe.
 * Ohne einen eigenen Haken faellt die Zaehlung unter „Form stimmt" und
 * „beliebiger Balkenstapel" gleich aus (Regel 17 §3b).
 *
 * `role="status"` + `aria-label` + `aria-busy` melden den Ladezustand
 * Hilfsmitteln an, wie es die uebrigen Gerueste unter `Skeletons/` tun.
 *
 * Werte aus `theme/tokens` — Rule 22 laesst neue Literale nicht zu.
 *
 * @props
 *   cards     — Zahl der Platzhalterkacheln (Vorgabe 3)
 *   paper     — `true` zeichnet `Paper variant="outlined"` statt `Panel`
 *   ariaLabel — zugaenglicher Name des Ladezustands (Vorgabe „Loading metrics")
 */
export default function KpiCardsSkeleton({ cards = 3, paper = false, ariaLabel = 'Loading metrics' }) {
  const theme = useTheme();
  const Flaeche = paper ? Paper : Panel;
  const flaecheProps = paper ? { variant: 'outlined' } : {};
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={SP.tight}
      role="status"
      aria-label={ariaLabel}
      aria-busy="true"
    >
      {[...Array(cards)].map((_, i) => (
        <Flaeche
          key={i}
          {...flaecheProps}
          data-skelett-kachel=""
          sx={{
            flex: 1,
            minWidth: 140,
            textAlign: 'center',
            p: SP.card,
            // Nur der `paper`-Zweig: `Panel` bringt seinen eigenen Radius mit
            // (`PANEL_RADIUS`), und ein zweiter hier wuerde ihn ueberschreiben.
            ...(paper ? { borderRadius: `${theme.shape.borderRadius * 2}px` } : null),
          }}
        >
          <Skeleton variant="text" width="45%" sx={{ mx: 'auto' }} />
          <Skeleton variant="rounded" width="60%" height={28} sx={{ mx: 'auto', mt: SP.chip }} />
        </Flaeche>
      ))}
    </Stack>
  );
}
